import {
  ExtendedFile,
  useBlobUpload,
  useCreatePackageMutation,
  useProjectId,
  useServiceName,
  useStartPipelineMutation,
} from '@consigli/hooks';
import {
  DocumentPackageType,
  JobType,
  PipelineName,
  SiteDueDiligenceFacilityType,
} from '@consigli/types';
import limit from 'p-limit';
import { Dispatch, SetStateAction, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { toast } from 'react-toastify';

import { Route } from '@/routes';

import { PackageCreationHook } from './use-create-insight-package';
import { GisUrl, TemplateMode, WizardFormData } from '../site-due-diligence/wizard-context';

export interface SiteDueDiligenceWizardData {
  facilityType: SiteDueDiligenceFacilityType | null;
  templateMode: TemplateMode | null;
  customTemplateFile: ExtendedFile | null;
  selectedCustomer: string | null;
  documentFiles: ExtendedFile[];
  gisFiles: ExtendedFile[];
  urls: GisUrl[];
  allowLLMForDocuments: boolean;
}

interface CreateSiteDueDiligenceParams {
  formData: WizardFormData;
  wizardData: SiteDueDiligenceWizardData;
}

/**
 * Convert facility type enum to kebab-case format expected by API
 */
export const convertFacilityTypeToKebabCase = (
  facilityType: SiteDueDiligenceFacilityType | null,
): string | null => {
  if (!facilityType) return null;
  // Convert SCREAMING_SNAKE_CASE to kebab-case (e.g., DATA_CENTER -> data-center)
  return facilityType.toLowerCase().replace(/_/g, '-');
};

export const useCreateSiteDueDiligencePackage = ({
  setCurrentCounter,
  setTargetCounter,
}: PackageCreationHook) => {
  const projectId = useProjectId();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const serviceName = useServiceName();
  const [createPackage] = useCreatePackageMutation();
  const [startPipeline] = useStartPipelineMutation();
  const { upload } = useBlobUpload();

  const uploadWithCounter = useCallback(
    async (file: ExtendedFile, documentPackageId: string) => {
      try {
        await upload(file, documentPackageId);
      } finally {
        setCurrentCounter((counter) => counter + 1);
      }
    },
    [upload, setCurrentCounter],
  );

  const uploadFiles = useCallback(
    async (files: ExtendedFile[], documentPackageId: string) => {
      setCurrentCounter(0);
      setTargetCounter(files.length);

      const queue = limit(100);
      const input = files.map((file) => queue(uploadWithCounter, file, documentPackageId));

      await Promise.allSettled(input);
    },
    [setCurrentCounter, setTargetCounter, uploadWithCounter],
  );

  const createSiteDueDiligencePackage = useCallback(
    async ({ formData, wizardData }: CreateSiteDueDiligenceParams) => {
      const { iterationName, selectedSiteLat, selectedSiteLon, language, employerRole } = formData;
      const {
        facilityType,
        templateMode,
        customTemplateFile,
        selectedCustomer,
        documentFiles,
        gisFiles,
        urls,
        allowLLMForDocuments,
      } = wizardData;

      // Step 1: Create the document package
      const packageName = iterationName || 'Site Due Diligence Analysis';

      // Build metadata for the site due diligence analysis
      // All data is stored in package.data and read by breeze from there
      const packageData: Record<string, unknown> = {
        facilityType: convertFacilityTypeToKebabCase(facilityType),
        templateMode,
        selectedCustomer:
          facilityType === SiteDueDiligenceFacilityType.DATA_CENTER ? selectedCustomer : undefined,
        locationLat: selectedSiteLat,
        locationLon: selectedSiteLon,
        urls: urls.length > 0 ? urls : undefined,
        allowLLMForDocuments,
      };

      const response = await createPackage({
        projectId,
        type: DocumentPackageType.SITE_DUE_DILIGENCE,
        name: packageName,
        language,
        employerRole,
        data: packageData,
      }).unwrap();

      if (!response?.id) {
        throw new Error('Failed to create package');
      }

      const packageId = response.id;

      // Step 2: Upload all files (documents, GIS files, and custom template)
      const allFiles: ExtendedFile[] = [...documentFiles, ...gisFiles];
      if (customTemplateFile) {
        allFiles.push(customTemplateFile);
      }

      if (allFiles.length > 0) {
        await uploadFiles(allFiles, packageId);
      }

      // Step 3: Start the analysis pipeline
      await startPipeline({
        projectId,
        packageId,
        pipelineName: PipelineName.DOCUMENT_PACKAGE_REPORTS,
        notify: true,
        jobType: JobType.DOCUMENT_PACKAGE,
        metadata: {},
      }).unwrap();

      return packageId;
    },
    [createPackage, startPipeline, uploadFiles, projectId],
  );

  const onAnalyze = useCallback(
    async (
      formData: WizardFormData,
      wizardData: SiteDueDiligenceWizardData,
      setIsProcessing: Dispatch<SetStateAction<boolean>>,
    ) => {
      try {
        setIsProcessing(true);

        const packageId = await createSiteDueDiligencePackage({ formData, wizardData });

        toast.success(t('package-creation.success-analyzed'));
        navigate(
          `/${Route.PROJECTS}/${projectId}/${Route.SERVICES}/${serviceName}/${Route.PACKAGES}/${packageId}/${Route.REPORTS}`,
        );
      } catch (error) {
        console.error('Error creating site due diligence package:', error);
        toast.error(t('package-creation.failed-analyzed'));
      } finally {
        setIsProcessing(false);
      }
    },
    [createSiteDueDiligencePackage, t, navigate, projectId, serviceName],
  );

  return {
    createSiteDueDiligencePackage,
    uploadFiles,
    onAnalyze,
  };
};
